<template>
  <div id="client-base" class="flex">
    <div v-if="pageLoading" class="fixed z-50 w-screen h-screen top-18">
      <div class="absolute w-full h-full opacity-75 bg-pure-white"></div>
      <div class="absolute z-10 grid w-full h-full place-items-center">
        <div class="blum-loader"></div>
      </div>
    </div>

    <div class="flex flex-col flex-grow">
      <div
        v-if="redirectTarget"
        class="sticky left-0 z-50 flex items-center flex-shrink-0 h-10 px-6 border-b border-slate-400 bg-pure-white"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <router-link :to="redirectTarget" class="flex items-center gap-2">
          <BlIcon icon="chevron-left" class="w-6 h-6" />
          <BlTextBody2
            :text="redirectLabel || redirectTarget.replaceAll('/', ' ')"
            class="capitalize-first"
          />
        </router-link>
      </div>

      <div
        id="client-header"
        class="sticky left-0 flex items-center justify-between flex-shrink-0 h-12 gap-4 px-6 bg-pure-white"
        :style="{ zIndex: 51, width: 'calc(100vw - 0.5rem)' }"
      >
        <BlBreadcrumbs :breadcrumbs="breadcrumbs" />

        <div v-if="client" class="h-7.5 flex items-center justify-end gap-4">
          <div
            v-if="client.accesses && client.accesses.length"
            class="flex items-center gap-4"
          >
            <BlIcon icon="people-therapist" class="w-6 h-6" />
            <div
              v-for="access in client.accesses.slice(0, 3)"
              :key="access.id"
              class="hidden lg:block"
            >
              <router-link
                v-if="access.user_id"
                :event="currentUser.role.includes('admin') ? 'click' : ''"
                target="_blank"
                :to="{
                  name: 'TeamSessionHistory',
                  params: {
                    user_id: access.user_id,
                    redirect_target: `/clients/${client.id}`,
                    redirect_label: 'Client profile'
                  }
                }"
              >
                <div
                  v-if="access.user && access.user.name"
                  class="flex flex-wrap items-center flex-shrink-0 h-8 px-2 truncate border rounded bg-success-100 border-success-100 max-w-64"
                  :class="
                    currentUser.role.includes('admin')
                      ? 'hover:border-success-400'
                      : 'cursor-default'
                  "
                >
                  <BlTextBody2
                    class="truncate text-success"
                    :text="access.user.name"
                  />
                </div>
              </router-link>
            </div>
            <div
              class="relative"
              v-if="
                client.accesses.slice(viewMode === 'desktop' ? 3 : 0).length
              "
            >
              <div
                class="flex items-center justify-center cursor-pointer"
                @click="openMoreTherapist = !openMoreTherapist"
              >
                <BlBubbleCount
                  :count="
                    `+ ${
                      client.accesses.slice(viewMode === 'desktop' ? 3 : 0)
                        .length
                    }`
                  "
                  class="h-7 hover:bg-light-purple-100"
                />
              </div>
              <div
                v-if="openMoreTherapist"
                class="absolute top-0 left-0 z-50 flex flex-col gap-1 p-1 mt-10 truncate border rounded lg:right-0 bg-pure-white border-grey-200 min-w-40"
              >
                <div
                  v-for="access in client.accesses.slice(
                    viewMode === 'desktop' ? 3 : 0
                  )"
                  :key="access.id"
                  class="flex-grow max-w-64"
                >
                  <router-link
                    v-if="access.user_id"
                    :event="currentUser.role.includes('admin') ? 'click' : ''"
                    target="_blank"
                    :to="{
                      name: 'TeamSessionHistory',
                      params: {
                        user_id: access.user_id,
                        redirect_target: `/clients/${client.id}`,
                        redirect_label: 'Client profile'
                      }
                    }"
                  >
                    <div
                      v-if="access.user && access.user.name"
                      class="px-4 py-2 text-left truncate rounded"
                      :class="[
                        currentUser.role.includes('admin')
                          ? 'hover:bg-hover-state'
                          : 'cursor-default'
                      ]"
                      :title="access.user.name"
                    >
                      <BlTextBody2
                        class="truncate text-grey"
                        :text="access.user.name"
                      />
                    </div>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="currentUser.role.includes('admin')"
            class="relative flex items-center flex-shrink-0 gap-4"
          >
            <div class="h-7.5 w-0.5 rounded bg-slate-400"></div>
            <BlInputButton
              @clicked="onOpenManageTherapist"
              type="outlined"
              dense
              :label="'Assign therapist'"
            />
            <BlInputButton
              @clicked="menuOpened = !menuOpened"
              icon="dots-vertical"
              class="flex items-center justify-center w-8 h-8 hover:bg-hover-state"
              type="plain"
            />
            <div
              v-if="menuOpened"
              class="absolute right-0 z-50 p-1 mt-2 space-y-1 border rounded top-full bg-pure-white border-grey-200"
            >
              <button
                @click.prevent="deleteOpened = true"
                class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state min-w-32"
              >
                <BlTextBody2 class="text-error-400" text="Delete client" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <BlBackdrop
        v-if="manageTherapistOpened"
        title="Manage therapist"
        description="Manage and assign therapist to specific client."
        label="Update"
        :disabled="!verifiedManageTherapistInput"
        :submitLoading="manageTherapistLoading"
        @backdrop-close="manageTherapistOpened = false"
        @backdrop-action="onManageTherapist"
      >
        <div class="flex flex-col gap-4 px-8" :style="{ width: '40rem' }">
          <BlSearchSelect
            asterisk
            label="Client's name"
            v-model="client.id"
            :options="[{ value: client.id, label: client.name }]"
            disabled
          />
          <BlSearchBox
            type="user"
            label="Assign to"
            v-model="manageTherapistInput.userIds"
            placeholder="Type your therapist's name"
            :options="
              manageTherapistInput.existingUsers.map(i => ({
                value: i.id,
                label: i.name
              }))
            "
            :fetchProperty="{
              url: `/api/v1/users?sort=name_asc&status=active&query=`,
              dataKey: 'users',
              countKey: 'total_users',
              optionKeys: { value: 'id', label: 'name' }
            }"
            @change="manageTherapistInput.userIds = $event"
          />
          <div
            v-if="diffManageTherapistCount > 0"
            class="flex items-start gap-2 px-3 py-2 text-sm rounded bg-pending-100 text-pending"
          >
            <BlIcon icon="warning" color="pending" class="flex-shrink-0 w-6" />
            <span class="flex-wrap">
              You are about to remove
              {{ diffManageTherapistCount }} therapist(s) from this client.
              <br />
              Once removed, they will no longer have access to this client's
              information.
              <br />
              Don't worry, you can always assign them again in the future.
            </span>
          </div>
        </div>
      </BlBackdrop>

      <BlBackdrop
        v-if="deleteOpened"
        title="Delete client"
        label="Delete client"
        :description="
          client.has_draft_sessions
            ? `This client has draft or ongoing sessions. Are you sure you want to delete the client? This action is irreversible.`
            : `You are about to delete client ${client.name}. Are you sure you want to proceed? This action is irreversible.`
        "
        type="error"
        empty
        :submitLoading="deleteLoading"
        @backdrop-close="deleteOpened = false"
        @backdrop-action="onRemoveClient"
      />

      <div
        class="sticky left-0 z-50 flex items-center flex-shrink-0 h-10 gap-4 px-6 overflow-x-auto border-b bg-pure-white border-slate-400"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <div
          v-for="tab in tabs"
          :key="tab.target"
          :class="{ 'bl-tab-actived': tab.isActive }"
          class="relative flex items-center flex-shrink-0 h-full gap-1 px-4 transition-all duration-300 rounded-t cursor-pointer bl-menu-tab-revamp hover:bg-hover-state"
          @click="changeTab(tab.target)"
        >
          <BlTextSubtitle2 v-if="tab.isActive" :text="tab.label" />
          <BlTextBody1 v-else :text="tab.label" />
          <BlBubbleCount v-if="tab.count" :count="tab.count" minified />
        </div>
      </div>

      <TargetJobProgression />

      <div
        v-if="client"
        class="flex flex-col flex-grow"
        :style="{ minHeight: 'calc(100vh - 4.5rem - 5rem - 0.5rem)' }"
      >
        <ClientAbout v-if="!$route.params.tab" :id="client.id" />
        <ClientCumulativeGraph
          v-if="$route.params.tab === 'cumulative-graph'"
          :id="client.id"
        />
        <ClientRecommendation
          v-if="$route.params.tab === 'recommendation'"
          :id="client.id"
        />
        <ClientTarget v-if="$route.params.tab === 'target'" :id="client.id" />
        <ClientTask
          v-if="$route.params.tab === 'task-analysis'"
          :id="client.id"
        />
        <ClientSession v-if="$route.params.tab === 'session'" :id="client.id" />
        <ClientCredit v-if="$route.params.tab === 'credit'" :id="client.id" />
        <ClientReport v-if="$route.params.tab === 'report'" :id="client.id" />
        <ClientFile
          v-if="$route.params.tab === 'file'"
          :id="client.id"
          :uploadProgress="uploadProgress"
          @set-upload-progress="uploadProgress = $event"
        />
      </div>

      <div
        v-if="uploadProgress.length"
        class="fixed z-50 right-6 bottom-6 w-128"
      >
        <div
          class="flex items-center gap-4 px-6 cursor-pointer h-14 rounded-t-3xl bg-light-purple-300"
          :class="{ 'rounded-b-3xl': !uploadChevronOpened }"
          @click="uploadChevronOpened = !uploadChevronOpened"
        >
          <BlIcon
            icon="chevron-down"
            color="secondary"
            class="w-5 h-5 transition-all duration-300"
            :style="{ rotate: !uploadChevronOpened ? '180deg' : '' }"
          />
          <BlTextSubtitle2
            v-if="uploadInProgress"
            :text="
              `Uploading ${uploadProgress.filter(i => i.started).length}/${
                uploadProgress.length
              } file(s)`
            "
            class="text-pure-white"
          />
          <div v-else class="flex items-center justify-between w-full">
            <BlTextSubtitle2
              :text="`${uploadedCount} file(s) uploaded`"
              class="text-pure-white"
            />
            <BlIcon
              icon="close"
              color="secondary"
              class="w-5 h-5"
              clickable
              @clicked="onClearProgress"
            />
          </div>
        </div>
        <div
          v-if="uploadChevronOpened"
          class="px-6 py-4 overflow-y-auto border bg-pure-white border-slate-400 rounded-b-3xl"
          :style="{ maxHeight: '75vh' }"
        >
          <div class="flex flex-col gap-2">
            <div>
              <BlTextBody2
                :text="
                  `To keep your upload active, please stay on this page. Moving within Blüm will stop the process, and any files that haven't finished uploading will be lost.`
                "
              />
            </div>
            <div
              v-for="progress in uploadProgress"
              :key="progress.name"
              class="flex items-center justify-between gap-4 px-3 py-3 border-b"
            >
              <div class="flex flex-col gap-1 truncate">
                <div
                  class="flex items-center gap-1 truncate"
                  :title="progress.name"
                >
                  <div class="truncate">
                    <BlTextSubtitle3 :text="progress.name" />
                  </div>
                  <div class="flex-shrink-0 w-1 h-1 rounded bg-slate-600"></div>
                  <BlTextBody2
                    :text="getFileSize(progress.size)"
                    class="flex-shrink-0 text-slate-600"
                  />
                </div>
                <BlTextBody2
                  v-if="progress.exceed_limit"
                  text="File exceeds 25 MB and cannot be uploaded."
                  class="flex-shrink-0 text-slate-600"
                />
              </div>
              <div
                class="flex items-center justify-end flex-shrink-0 gap-2 w-14"
              >
                <div v-if="progress.uploading" class="blum-loader-btn"></div>
                <BlIcon
                  v-if="
                    !progress.uploading &&
                      (!progress.started ||
                        !progress.success ||
                        progress.exceed_limit)
                  "
                  icon="close"
                  class="w-4 h-4"
                  clickable
                  @clicked="onRemoveProgress(progress)"
                />
                <BlIcon
                  v-if="progress.success"
                  icon="check-circle"
                  color="success-4"
                  class="w-4 h-4"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import TargetJobProgression from "./target/TargetJobProgression.vue";
import ClientAbout from "./ClientAbout.vue";
import ClientCumulativeGraph from "./ClientCumulativeGraph.vue";
import ClientRecommendation from "./ClientRecommendation.vue";
import ClientTarget from "./ClientTarget.vue";
import ClientTask from "./ClientTask.vue";
import ClientSession from "./ClientSession.vue";
import ClientCredit from "./ClientCredit.vue";
import ClientReport from "./ClientReport.vue";
import ClientFile from "./ClientFile.vue";

import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlBreadcrumbs from "../BlBreadcrumbs.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlIcon from "../BlIcon.vue";
import BlInputButton from "../BlInputButton.vue";
import BlSearchSelect from "../BlSearchSelect.vue";
import BlSearchBox from "../BlSearchBox.vue";
import BlBackdrop from "../BlBackdrop.vue";

export default {
  name: "Client",
  components: {
    TargetJobProgression,
    ClientAbout,
    ClientCumulativeGraph,
    ClientRecommendation,
    ClientTarget,
    ClientTask,
    ClientSession,
    ClientCredit,
    ClientReport,
    ClientFile,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody1,
    BlBreadcrumbs,
    BlBubbleCount,
    BlIcon,
    BlInputButton,
    BlSearchSelect,
    BlSearchBox,
    BlBackdrop
  },
  data() {
    return {
      redirectTarget: "",
      redirectLabel: "",
      //
      id: "",
      pageLoading: true,
      openMoreTherapist: false,
      menuOpened: false,
      deleteOpened: false,
      deleteLoading: false,
      manageTherapistOpened: false,
      manageTherapistLoading: false,
      manageTherapistInput: {
        id: "",
        userIds: [],
        existingUserIds: [],
        existingUsers: []
      },
      //
      uploadChevronOpened: true,
      uploadProgress: [
        // {
        //   name: "satu.png",
        //   size: 114664,
        //   exceed_limit: false,
        //   started: false,
        //   uploading: false,
        //   success: false
        // },
      ]
    };
  },
  computed: {
    ...mapGetters([
      "viewMode",
      "currentUser",
      "currentCenter",
      "client",
      "users",
      "totalActionRecommendations"
    ]),
    enableAppointment() {
      return this.currentCenter.enable_appointment;
    },
    breadcrumbs() {
      const clientId = Number(this.$route.params.id);
      const clientLabel =
        (this.client && this.client.id) !== clientId
          ? "Client name"
          : this.client.name;
      return [
        { label: "Clients", target: `/clients` },
        { label: clientLabel, target: `/clients/${clientId}`, truncate: true }
      ];
    },
    tabs() {
      return [
        { target: "", isActive: !this.$route.params.tab, label: "About" },
        {
          target: "cumulative-graph",
          isActive: this.$route.params.tab === "cumulative-graph",
          label: "Cumulative Graph"
        },
        {
          target: "recommendation",
          isActive: this.$route.params.tab === "recommendation",
          label: "Recommendations",
          count: this.totalActionRecommendations
        },
        {
          target: "target",
          isActive: this.$route.params.tab === "target",
          label: "Targets"
        },
        {
          target: "task-analysis",
          isActive: this.$route.params.tab === "task-analysis",
          label: "Tasks Analysis"
        },
        {
          target: "session",
          isActive: this.$route.params.tab === "session",
          label: "Sessions"
        },
        {
          target: "credit",
          isActive: this.$route.params.tab === "credit",
          label: "Credits"
        },
        {
          target: "report",
          isActive: this.$route.params.tab === "report",
          label: "Reports"
        },
        {
          target: "file",
          isActive: this.$route.params.tab === "file",
          label: "Files"
        }
      ];
    },
    diffManageTherapistCount() {
      const existingUserIds = this.manageTherapistInput.existingUserIds;
      const userIds = this.manageTherapistInput.userIds;
      return this.differenceTwoArray(existingUserIds, userIds).length;
    },
    verifiedManageTherapistInput() {
      if (this.manageTherapistLoading) return false;
      if (!this.manageTherapistInput.id) return false;
      const userIds = this.manageTherapistInput.userIds;
      const existingUserIds = this.manageTherapistInput.existingUserIds;
      const ab = this.differenceTwoArray(userIds, existingUserIds);
      const ba = this.differenceTwoArray(existingUserIds, userIds);
      if (!ab.length && !ba.length) return false;
      return true;
    },
    uploadInProgress() {
      const loadingIndex = this.uploadProgress.findIndex(i => i.uploading);
      return loadingIndex > -1;
    },
    uploadedCount() {
      return this.uploadProgress.filter(i => i.success).length;
    }
  },
  watch: {
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    },
    openMoreTherapist(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerMore);
        }, 10);
      }
    }
  },
  created() {
    this.redirectTarget = this.$route.params.redirect_target || "";
    this.redirectLabel = this.$route.params.redirect_label || "";

    this.getBranches({ params: "?sort=name_asc" });
    this.getLabels({ params: "?sort=name_asc" });
    this.getUsers({ params: `?sort=name_asc&status=active` });
  },
  mounted() {
    this.pageLoading = true;
    this.id = this.$route.params.id;
    this.getClient({ id: this.id }).then(({ success, data }) => {
      this.pageLoading = false;
      if (!success) return;
      if (data.running_create_target_job) {
        const check = {
          client_id: data.id,
          job_id: data.running_create_target_job.job_id
        };
        this.checkTargetJob(check);
      }
    });
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) app.scroll({ top: 0, left: 0 });
  },
  methods: {
    ...mapActions([
      "getBranches",
      "getLabels",
      "getUsers",
      "getClient",
      "deleteClient",
      "updateClient",
      "checkTargetJob"
    ]),
    changeTab(tab) {
      if (!tab) this.$router.push({ path: `/clients/${this.id}` });
      else this.$router.push({ path: `/clients/${this.id}/${tab}` });
    },
    async onRemoveClient() {
      this.deleteLoading = true;
      const { success } = await this.deleteClient(this.client);
      this.deleteLoading = false;
      if (!success) return;
      this.deleteOpened = false;
      this.$router.push({ path: "/clients" });
    },
    //
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.menuOpened = false;
    },
    closeHandlerMore() {
      window.removeEventListener("click", this.closeHandlerMore);
      this.openMoreTherapist = false;
    },
    onOpenManageTherapist() {
      this.manageTherapistOpened = true;
      this.resetManageTherapist();
    },
    resetManageTherapist() {
      const accesses = this.client.accesses ? this.client.accesses : [];
      const users = accesses.map(i => i.user);
      const ids = accesses.map(i => Number(i.user_id));
      this.manageTherapistInput = {
        id: this.client.id,
        userIds: ids,
        existingUserIds: ids,
        existingUsers: users
      };
    },
    differenceTwoArray(a = [], b = []) {
      return a.filter(i => !b.includes(i));
    },
    async onManageTherapist() {
      this.manageTherapistLoading = true;
      const { success } = await this.updateClient({
        id: this.manageTherapistInput.id,
        user_ids: this.manageTherapistInput.userIds
      });
      this.manageTherapistLoading = false;
      if (!success) return;
      this.manageTherapistOpened = false;
    },
    //
    getFileSize(sizeinbytes) {
      const fSExt = ["Bytes", "KB", "MB", "GB"];
      let fSize = sizeinbytes;
      let i = 0;
      while (fSize > 900) {
        fSize /= 1024;
        i++;
      }

      return Math.round(fSize * 100) / 100 + " " + fSExt[i];
    },
    onClearProgress() {
      this.uploadProgress = [];
    },
    onRemoveProgress(val) {
      this.uploadProgress = this.uploadProgress.filter(
        i => i.name !== val.name
      );
    }
  }
};
</script>

<style scoped lang="scss">
.bl-menu-tab-revamp {
  &.bl-tab-actived::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: var(--primary-color);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/Client.vue